import { type ReactNode, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Check, X, GripVertical } from 'lucide-react';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Separator } from '@/shared/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { cn } from '@/core/lib/utils';
import { getColorValue } from '@/core/lib/color-contrast';
import { useProjectStatuses } from '../../hooks/useProjectStatuses';
import {
  useCreateBoardStatus,
  useUpdateBoardStatus,
  useDeleteBoardStatus,
  useReorderBoardStatuses,
} from '@/features/boards/public';
import type { ProjectStatus, TaskStatusType } from '../../types';
import type { StatusType } from '@/features/boards/public';

const STATUS_TYPE_OPTIONS: StatusType[] = ['Todo', 'InProgress', 'Done'];

const TASK_TO_BOARD_TYPE: Record<TaskStatusType, StatusType> = {
  todo: 'Todo',
  'in-progress': 'InProgress',
  done: 'Done',
};

// ── Internal: single row in a group ──────────────────────────────────────────

interface StatusOptionRowProps {
  status: ProjectStatus;
  groupType: TaskStatusType;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function StatusOptionRow({
  status,
  groupType,
  isActive,
  onSelect,
  onEdit,
  onDelete,
}: StatusOptionRowProps) {
  const { t } = useTranslation('tasks');
  const [hovered, setHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const bg = getColorValue(status.color ?? '');

  useEffect(() => {
    const el = itemRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return;
    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ statusId: status.id, groupType }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
    const cleanupDrop = dropTargetForElements({
      element: el,
      getData: () => ({ statusId: status.id, groupType }),
    });
    return () => {
      cleanupDraggable();
      cleanupDrop();
    };
  }, [status.id, groupType]);

  return (
    <div
      ref={itemRef}
      data-testid={`tasks-status-picker-item-${status.id}`}
      className={cn(
        'flex cursor-pointer items-center gap-1.5 rounded px-1 py-1.5 hover:bg-accent',
        isActive && 'bg-accent/40',
        isDragging && 'opacity-40',
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
    >
      <button
        type="button"
        ref={handleRef}
        className="cursor-grab touch-none text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing"
        style={{ opacity: hovered ? 1 : 0 }}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
      >
        <GripVertical className="size-3" />
      </button>
      <span
        className="size-2 shrink-0 rounded-full"
        style={{ background: bg ?? 'hsl(var(--muted-foreground))' }}
      />
      <span className={cn('flex-1 text-xs', isActive && 'font-medium')}>{status.name}</span>
      {hovered && (
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                data-testid={`tasks-status-picker-item-edit-${status.id}`}
                onClick={onEdit}
                aria-label={t('statusPicker.editAria')}
              >
                <Pencil className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('statusPicker.editAria')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 hover:text-destructive"
                data-testid={`tasks-status-picker-item-delete-${status.id}`}
                onClick={onDelete}
                aria-label={t('statusPicker.deleteAria')}
              >
                <Trash2 className="size-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('statusPicker.deleteAria')}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

// ── Internal: inline edit form ────────────────────────────────────────────────

interface StatusEditRowProps {
  status: ProjectStatus;
  onSave: (name: string, color: string) => void;
  onCancel: () => void;
}

function StatusEditRow({ status, onSave, onCancel }: StatusEditRowProps) {
  const { t } = useTranslation('tasks');
  const [name, setName] = useState(status.name ?? '');
  const [color, setColor] = useState(status.color ?? '');

  return (
    <div className="rounded border bg-muted/30 px-2.5 py-2 mx-1 my-0.5">
      <div className="flex items-center gap-2.5">
        <ColorPickerPopover
          value={color}
          onSelect={setColor}
          trigger={
            <button
              type="button"
              data-testid="tasks-status-picker-edit-color"
              className="size-5 shrink-0 rounded-full border border-border transition-opacity hover:opacity-70"
              style={{ background: getColorValue(color) ?? 'hsl(var(--muted-foreground))' }}
            />
          }
        />
        <Input
          autoFocus
          data-testid="tasks-status-picker-edit-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter' && name.trim()) onSave(name.trim(), color);
            if (e.key === 'Escape') onCancel();
          }}
          placeholder={t('statusPicker.namePlaceholder')}
          className="h-6 flex-1 text-xs"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              data-testid="tasks-status-picker-edit-save"
              onClick={() => name.trim() && onSave(name.trim(), color)}
              disabled={!name.trim()}
              aria-label={t('statusPicker.saveAria')}
            >
              <Check className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('statusPicker.saveAria')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              data-testid="tasks-status-picker-edit-cancel"
              onClick={onCancel}
              aria-label={t('statusPicker.cancelAria')}
            >
              <X className="size-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('statusPicker.cancelAria')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Internal: create form at popover bottom ───────────────────────────────────

interface StatusCreateFormProps {
  onSave: (name: string, color: string, type: StatusType) => void;
  onCancel: () => void;
}

function StatusCreateForm({ onSave, onCancel }: StatusCreateFormProps) {
  const { t } = useTranslation('tasks');
  const [name, setName] = useState('');
  const [color, setColor] = useState('indigo-violet');
  const [type, setType] = useState<StatusType>('Todo');

  return (
    <div className="border-t px-2.5 pt-2.5 pb-2.5">
      <div className="flex items-center gap-2.5 mb-2.5">
        <ColorPickerPopover
          value={color}
          onSelect={setColor}
          trigger={
            <button
              type="button"
              data-testid="tasks-status-picker-create-color"
              className="size-5 shrink-0 rounded-full border border-border transition-opacity hover:opacity-70"
              style={{ background: getColorValue(color) ?? 'hsl(var(--muted-foreground))' }}
            />
          }
        />
        <Input
          autoFocus
          data-testid="tasks-status-picker-create-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') onCancel();
          }}
          placeholder={t('statusPicker.namePlaceholder')}
          className="h-7 min-w-0 flex-1 text-xs"
        />
        <Select value={type} onValueChange={(v) => setType(v as StatusType)}>
          <SelectTrigger
            data-testid="tasks-status-picker-create-category"
            className="h-7 w-28 shrink-0 text-xs"
          >
            <SelectValue placeholder={t('statusPicker.categoryLabel')} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt} className="text-xs">
                {t(`statusPicker.category${opt}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-7 flex-1 text-xs"
          data-testid="tasks-status-picker-create-cancel"
          onClick={onCancel}
        >
          {t('statusPicker.cancelAria')}
        </Button>
        <Button
          size="sm"
          className="h-7 flex-1 text-xs"
          data-testid="tasks-status-picker-create-save"
          onClick={() => name.trim() && onSave(name.trim(), color, type)}
          disabled={!name.trim()}
        >
          {t('statusPicker.createBtn')}
        </Button>
      </div>
    </div>
  );
}

// ── Internal: group section ───────────────────────────────────────────────────

interface StatusGroupProps {
  type: TaskStatusType;
  statuses: ProjectStatus[];
  currentStatusId: string;
  editingStatusId: string | null;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (status: ProjectStatus) => void;
  onSaveEdit: (id: string, name: string, color: string) => void;
  onCancelEdit: () => void;
}

function StatusGroup({
  type,
  statuses,
  currentStatusId,
  editingStatusId,
  onSelect,
  onEdit,
  onDelete,
  onSaveEdit,
  onCancelEdit,
}: StatusGroupProps) {
  const { t } = useTranslation('tasks');
  const dropRef = useRef<HTMLDivElement>(null);

  const labelKey: Record<TaskStatusType, string> = {
    todo: 'groupTodo',
    'in-progress': 'groupInProgress',
    done: 'groupDone',
  };

  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    return dropTargetForElements({
      element: el,
      getData: () => ({ groupType: type }),
    });
  }, [type]);

  if (statuses.length === 0) return null;

  return (
    <div data-testid={`tasks-status-picker-group-${type}`}>
      <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {t(`statusPicker.${labelKey[type]}`)}
      </div>
      <div ref={dropRef} className="px-1 pb-1">
        {statuses.map((status) =>
          editingStatusId === status.id ? (
            <StatusEditRow
              key={status.id}
              status={status}
              onSave={(name, color) => onSaveEdit(status.id, name, color)}
              onCancel={onCancelEdit}
            />
          ) : (
            <StatusOptionRow
              key={status.id}
              status={status}
              groupType={type}
              isActive={status.id === currentStatusId}
              onSelect={() => onSelect(status.id)}
              onEdit={() => onEdit(status.id)}
              onDelete={() => onDelete(status)}
            />
          ),
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface StatusPickerProps {
  boardId: string;
  currentStatus?: ProjectStatus;
  onSelect?: (statusId: string) => void;
  trigger?: ReactNode;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultEditStatusId?: string;
}

export function StatusPicker({
  boardId,
  currentStatus,
  onSelect,
  trigger,
  disabled,
  open: controlledOpen,
  onOpenChange: onControlledOpenChange,
  defaultEditStatusId,
}: StatusPickerProps) {
  const { t } = useTranslation('tasks');
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const [search, setSearch] = useState('');
  const [editingStatusId, setEditingStatusId] = useState<string | null>(
    defaultEditStatusId ?? null,
  );
  const [creating, setCreating] = useState(false);

  const { statuses } = useProjectStatuses(boardId);
  const createStatus = useCreateBoardStatus(boardId);
  const updateStatus = useUpdateBoardStatus(boardId);
  const deleteStatus = useDeleteBoardStatus(boardId);
  const reorderStatuses = useReorderBoardStatuses(boardId);
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  useEffect(() => {
    return monitorForElements({
      onDrop({ source, location }) {
        const targets = location.current.dropTargets;
        if (targets.length === 0) return;

        const sourceData = source.data as { statusId?: string; groupType?: TaskStatusType };
        if (!sourceData.statusId || !sourceData.groupType) return;

        const sourceId = sourceData.statusId;
        const sourceGroup = sourceData.groupType;

        const innerData = targets[0].data as { statusId?: string; groupType?: TaskStatusType };
        if (!innerData.groupType) return;

        const targetGroup = innerData.groupType;

        if (innerData.statusId === sourceId) return;

        const sourceStatus = statuses.find((s) => s.id === sourceId);
        if (!sourceStatus) return;

        const base = statuses
          .filter((s) => s.type === targetGroup && s.id !== sourceId)
          .slice()
          .sort((a, b) => a.order - b.order);

        if (innerData.statusId) {
          const idx = base.findIndex((s) => s.id === innerData.statusId);
          base.splice(idx === -1 ? base.length : idx, 0, sourceStatus);
        } else {
          base.push(sourceStatus);
        }

        const orderedIds = base.map((s) => s.id);

        if (sourceGroup !== targetGroup) {
          updateStatus.mutate({
            statusId: sourceId,
            data: { type: TASK_TO_BOARD_TYPE[targetGroup] },
          });
        }

        reorderStatuses.mutate({ orderedIds });
      },
    });
  }, [statuses, updateStatus, reorderStatuses]);

  const filtered = search
    ? statuses.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : statuses;

  const groupedStatuses: Record<TaskStatusType, ProjectStatus[]> = {
    todo: filtered.filter((s) => s.type === 'todo').sort((a, b) => a.order - b.order),
    'in-progress': filtered
      .filter((s) => s.type === 'in-progress')
      .sort((a, b) => a.order - b.order),
    done: filtered.filter((s) => s.type === 'done').sort((a, b) => a.order - b.order),
  };

  function handleSelect(statusId: string) {
    if (currentStatus && statusId !== currentStatus.id) onSelect?.(statusId);
    handleOpenChange(false);
  }

  async function handleDelete(status: ProjectStatus) {
    const { confirmed } = await confirm({
      title: t('statusPicker.confirmDeleteTitle'),
      description: t('statusPicker.confirmDeleteDesc'),
      confirmLabel: t('statusPicker.confirmDeleteConfirm'),
      cancelLabel: t('statusPicker.confirmDeleteCancel'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    deleteStatus.mutate(status.id);
  }

  async function handleCreate(name: string, color: string, type: StatusType) {
    await createStatus.mutateAsync({ name, color, type });
    setCreating(false);
  }

  function handleSaveEdit(statusId: string, name: string, color: string) {
    const status = statuses.find((s) => s.id === statusId);
    const type = status ? TASK_TO_BOARD_TYPE[status.type] : 'Todo';
    updateStatus.mutate({ statusId, data: { name, color, type } });
    setEditingStatusId(null);
  }

  function handleOpenChange(v: boolean) {
    if (!isControlled) setInternalOpen(v);
    onControlledOpenChange?.(v);
    if (!v) {
      setSearch('');
      setEditingStatusId(null);
      setCreating(false);
    }
  }

  // Resolve the displayed status from the live list by id — `currentStatus` is a
  // denormalized snapshot (e.g. from `task.status`) that goes stale after a rename.
  const liveCurrentStatus = currentStatus
    ? (statuses.find((s) => s.id === currentStatus.id) ?? currentStatus)
    : currentStatus;
  const currentBg = liveCurrentStatus ? getColorValue(liveCurrentStatus.color ?? '') : null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ?? (
          <button
            type="button"
            data-testid="tasks-status-picker-trigger"
            className="flex items-center gap-1.5 text-xs transition-opacity hover:opacity-70"
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: currentBg ?? 'hsl(var(--muted-foreground))' }}
              aria-hidden
            />
            {liveCurrentStatus?.name}
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        className={cn('p-0', creating ? 'w-80' : 'w-64')}
        align="start"
        data-testid="tasks-status-picker-popover"
      >
        <div className="px-3 py-2 text-xs font-semibold">{t('statusPicker.title')}</div>
        <Separator />

        <div className="px-2 pt-2 pb-1">
          <Input
            data-testid="tasks-status-picker-search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setEditingStatusId(null);
            }}
            placeholder={t('statusPicker.searchPlaceholder')}
            className="h-7 text-xs"
          />
        </div>

        <div className="max-h-60 overflow-y-auto">
          {(['todo', 'in-progress', 'done'] as TaskStatusType[]).map((type) => (
            <StatusGroup
              key={type}
              type={type}
              statuses={groupedStatuses[type]}
              currentStatusId={currentStatus?.id ?? ''}
              editingStatusId={editingStatusId}
              onSelect={handleSelect}
              onEdit={(id) => {
                setEditingStatusId(id);
                setCreating(false);
              }}
              onDelete={handleDelete}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={() => setEditingStatusId(null)}
            />
          ))}
        </div>

        {!creating ? (
          <button
            type="button"
            data-testid="tasks-status-picker-create-btn"
            className="flex w-full items-center gap-1.5 border-t px-3 py-2 text-xs text-primary hover:bg-accent"
            onClick={() => {
              setCreating(true);
              setEditingStatusId(null);
            }}
          >
            {t('statusPicker.createNew')}
          </button>
        ) : (
          <StatusCreateForm onSave={handleCreate} onCancel={() => setCreating(false)} />
        )}

        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(v) => {
            if (!v) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          variant={confirmOptions?.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </PopoverContent>
    </Popover>
  );
}
